import { AsyncPipe, NgClass } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    type OnInit,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterModule } from '@angular/router';
import { BreadcrumbModule } from '@openng/optimus-ui/breadcrumb';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { DrawerModule } from '@openng/optimus-ui/drawer';
import { OverlayBadgeModule } from '@openng/optimus-ui/overlaybadge';
import { type Popover, PopoverModule } from '@openng/optimus-ui/popover';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import moment from 'moment';
import { type Observable, timer } from 'rxjs';
import { filter, map, take, takeWhile } from 'rxjs/operators';
import { SidebarNavComponent } from '../sidebar-nav/sidebar-nav.component';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { DialogBoxComponent } from '@app/shared/components/dialog-box/dialog-box.component';
import { ProfilePopoverComponent } from '@app/shared/components/profile-popover/profile-popover.component';
import { ProjectListComponent } from '@app/shared/components/project-list/project-list.component';
import { RefreshJobDataComponent } from '@app/shared/components/refresh-job-data/refresh-job-data.component';
import { StatusIconComponent } from '@app/shared/components/status-icon/status-icon.component';
import { MODULE_ROUTE_PATH, MODULES } from '@app/shared/constants/shared.constant';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { BreadcrumbService } from '@app/shared/services/breadcrumb.service';
import { GoogleAnalyticsService } from '@app/shared/services/google-analytics.service';
import { JobBoardService } from '@app/shared/services/job-board.service';
import { PermissionService } from '@app/shared/services/permission.service';
import { ThemeService } from '@app/shared/services/theme.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { JobBoardComponent } from '@app/theme/job-board/job-board.component';
import { GlobalSearchComponent } from '@app/theme/layouts/header-nav/global-search/global-search.component';

@Component({
    selector: 'cor-header-nav',
    templateUrl: './header-nav.component.html',
    imports: [
        RouterModule,
        FormsModule,
        DialogBoxComponent,
        DialogModule,
        DrawerModule,
        RouterLink,
        TooltipModule,
        GlobalSearchComponent,
        PopoverModule,
        RefreshJobDataComponent,
        SidebarNavComponent,
        BreadcrumbModule,
        OverlayBadgeModule,
        ButtonModule,
        AsyncPipe,
        NgClass,
        StatusIconComponent,
        ProfilePopoverComponent,
        JobBoardComponent,
        ProjectListComponent,
    ],
})
export class HeaderNavComponent implements OnInit {
    readonly unreadNotificationCount = input<string>();
    onNotiClick = output<void>();

    protected readonly jobBoardOverlay = viewChild<Popover>('jobBoardOverlay');

    protected MODULES = MODULES;
    protected MODULE_ROUTE_PATH = MODULE_ROUTE_PATH;

    protected readonly themeService = inject(ThemeService);
    private readonly breadcrumbData = inject(BreadcrumbService).breadcrumbData;
    protected appConfigService = inject(AppConfigService);
    private readonly authService = inject(AuthenticationService);
    private readonly googleAnalyticService = inject(GoogleAnalyticsService);
    protected readonly permissionService = inject(PermissionService);
    protected readonly router = inject(Router);
    private readonly toast = inject(ToastNotificationService);
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly jobBoardService = inject(JobBoardService);

    // Token expire should be triggered from FE to BE, header should be the application shell
    protected readonly isTimeoutReminderVisible = signal(false);
    // The timeoutId to manage the popup for "manual" refresh flow when the user is idle
    private reminderPopupStartTimeoutId: ReturnType<typeof setTimeout>;
    // The timeoutId to manage the auto-closing for the popup for "manual" refresh flow
    private reminderPopupStopTimeoutId: ReturnType<typeof setTimeout>;
    protected countDown$: Observable<string>;
    protected readonly isTokenRefreshing = signal(false);
    protected readonly showSideMenu = signal(false);
    protected readonly isSearchPanelVisible = signal(false);

    protected readonly jobBoard = {
        cards: this.jobBoardService.cards.asReadonly(),
        isDialogVisible: signal(false),
        totalCount: computed(() => {
            // FIXME: used to listen to job-card changes.
            this.jobBoardService.lastUpdatedTime();
            return this.jobBoardService.cards().find(e => e.name === 'TOTAL').count;
        }),
    };

    protected readonly breadcrumbItems = computed(() =>
        this.breadcrumbData().map((b, i) => ({
            label: b.label,
            icon: b.icon,
            routerLink: i < this.breadcrumbData().length - 1 && b.url ? b.url : undefined,
        })),
    );

    protected readonly isHomePage = signal(
        new URL(this.router.url, window.origin).pathname === '/home',
    );

    ngOnInit() {
        this.router.events
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(e => e instanceof NavigationEnd),
            )
            .subscribe(e => {
                this.jobBoardOverlay()?.hide();
                this.isHomePage.set(
                    new URL(e.urlAfterRedirects, window.origin).pathname === '/home',
                );
            });

        this.authService.registerTokenListener();
        this.authService.currentUser$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(currentUser => {
                this.googleAnalyticService.setUserContext({
                    userId: currentUser?.id,
                    email: currentUser?.email,
                });
            });

        this.authService.tokenChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
            if (val === null) {
                return;
            }
            this.triggerReminderConfirmationBox();
        });

        this.jobBoardService.fetchJobBoardData();
    }

    protected onNotificationIconClick() {
        this.onNotiClick.emit();
    }

    /**
     * @summary This function is added as a functionality to manual logout button feature
     */
    private triggerReminderConfirmationBox() {
        this.clearReminderTimeouts();
        this.isTimeoutReminderVisible.set(false); // close the pop up if already open
        if (!this.authService.getToken()) {
            // user is not logged in - nothing to be done
            // Logged-in user check is important as interceptor starts to register lastUserInteraction before login
            // and updates even after logout
            return;
        }
        if (this.appConfigService.appConfig().manualRefreshReminderTimeout > 0) {
            const remainingTimer =
                this.authService.getSecondsToExpiry() -
                this.appConfigService.appConfig().manualRefreshReminderTimeout;
            // sets the timer for pop up to show after 25min when accessTimeout is 30 and manualRefreshReminderTimeout is 5min
            this.reminderPopupStartTimeoutId = setTimeout(() => {
                this.isTimeoutReminderVisible.set(true);

                // This is to hide the shown continue-user modal box
                const tokenTimeoutTimer = this.authService.getSecondsToExpiry();

                this.countDown$ = timer(0, 1000).pipe(
                    takeWhile(() => this.isTimeoutReminderVisible()),
                    take(tokenTimeoutTimer),
                    map(x => {
                        const seconds = tokenTimeoutTimer - x;

                        return moment.utc(seconds * 1000).format('mm:ss');
                    }),
                );
                this.reminderPopupStopTimeoutId = setTimeout(() => {
                    if (this.isTimeoutReminderVisible()) {
                        // Only logout if the reminder is visible. This is to ensure that there are no
                        // race conditions or edge cases where this setTimeout has not been cleared properly
                        this.logoutUser();
                    }
                }, tokenTimeoutTimer * 1000);
            }, remainingTimer * 1000);
        }
    }

    private clearReminderTimeouts() {
        clearTimeout(this.reminderPopupStartTimeoutId); // clear previous timer
        clearTimeout(this.reminderPopupStopTimeoutId); // clear previous timer
        this.reminderPopupStartTimeoutId = undefined;
        this.reminderPopupStopTimeoutId = undefined;
    }

    /**
     * @summary Function triggers an event that the user wants to continue the session
     */
    protected continueUserSession() {
        this.clearReminderTimeouts();
        this.isTokenRefreshing.set(true);
        this.authService
            .refreshToken()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: response => {
                    this.authService.saveTokens(response);
                },
                error: () => {
                    this.toast.showError('Failed to continue session.');
                },
            })
            .add(() => {
                this.isTokenRefreshing.set(false);
                this.isTimeoutReminderVisible.set(false); // close the pop up
            });
    }

    /**
     * @summary Logout logged-in user
     */
    protected logoutUser() {
        this.clearReminderTimeouts();
        this.isTimeoutReminderVisible.set(false); // close the pop up
        this.router.navigate(['/logout'], {
            queryParams: { returnUrl: this.router.routerState.snapshot.url },
        });
    }
}
